import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, MessageCircle, ChevronDown, Sparkles, ShieldCheck, Headphones, HardHat } from "lucide-react";
import { useSEO } from "../../lib/seo";
import { BRAND } from "../../data/content";

/**
 * LocationLandingPage — drives /smart-home/:city pages.
 *
 * Visual language: city skyline gradient + glassmorphism + gold.
 * Includes: hero, areas served, services, case studies, why GenieX, FAQ, CTA.
 */
export default function LocationLandingPage({ slug, content, onBook }) {
  useSEO({
    title: content.title,
    description: content.intro,
    path: `/smart-home/${slug}`,
    keywords: content.keywords,
  });

  const waLink = `https://wa.me/${BRAND.phoneRaw.replace("+", "")}?text=${encodeURIComponent(
    `Hi GenieX, I'd like to enquire about smart home automation in ${slug.charAt(0).toUpperCase() + slug.slice(1)}.`
  )}`;

  return (
    <main data-testid={`location-page-${slug}`} className="bg-[var(--gx-bg)] text-white overflow-x-hidden">
      {/* HERO */}
      <section className="relative min-h-[80svh] flex items-end pt-28 lg:pt-36 pb-16 lg:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(212,175,55,0.18),transparent_55%)]" />
        <SkylineGraphic />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--gx-bg)]/30 to-[var(--gx-bg)]" />

        <div className="relative max-w-[1400px] w-full mx-auto px-5 lg:px-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/10 text-[11px] tracking-[0.18em] uppercase text-amber-100">
            <MapPin className="w-3 h-3" /> {content.eyebrow}
          </div>
          <h1 className="font-display tracking-tight text-white mt-7 text-4xl sm:text-5xl lg:text-[68px] leading-[1.04] max-w-[22ch]">
            {content.tagline}
          </h1>
          <p className="lead mt-7 max-w-[60ch]">{content.intro}</p>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            <button onClick={onBook} data-testid="hero-cta-book" className="btn-gold">
              Book a Free Site Survey <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="btn-outline"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="hero-cta-call"
              className="text-[12px] tracking-wide text-dim hover:text-gold transition-colors inline-flex items-center gap-2 ml-2"
            >
              <Phone className="w-3.5 h-3.5" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* AREAS SERVED */}
      <section className="border-t border-line/70">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Areas Served" title={`Citywide. End to end.`} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-12">
            {content.areas.map((a, i) => (
              <div
                key={a}
                data-testid={`area-${slugify(a)}`}
                className="group relative rounded-2xl border border-line bg-[var(--gx-surface)]/40 backdrop-blur-md p-5 hover:border-gold/40 transition-colors"
              >
                <div className="text-[10px] tracking-[0.32em] text-mute uppercase mb-2">0{(i + 1).toString().padStart(2, "0")}</div>
                <div className="text-[14px] text-white font-medium flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gold/70" />
                  {a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-line/70 bg-gradient-to-b from-[var(--gx-bg)] via-[#0B0B10] to-[var(--gx-bg)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Services" title="Everything we deliver locally." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {content.services.map((s, i) => (
              <div key={s} className="rounded-2xl border border-line bg-[var(--gx-surface)]/40 backdrop-blur-md p-6">
                <div className="text-[11px] tracking-[0.32em] text-mute uppercase mb-3">Service · {(i + 1).toString().padStart(2, "0")}</div>
                <div className="text-white text-[15px] font-medium leading-snug">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      {content.caseStudies?.length > 0 && (
        <section className="border-t border-line/70">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
            <SectionHeading eyebrow="Case Studies" title="From this neighbourhood." />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
              {content.caseStudies.map((c) => (
                <div
                  key={c.title}
                  data-testid={`case-${slugify(c.title)}`}
                  className="relative rounded-2xl border border-line bg-[var(--gx-surface)]/40 backdrop-blur-md p-8 overflow-hidden"
                >
                  <Sparkles className="w-4 h-4 text-gold mb-5" />
                  <h3 className="font-display text-xl tracking-tight">{c.title}</h3>
                  <p className="text-dim text-[14px] mt-3 leading-relaxed">{c.body}</p>
                  <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-gradient-to-br from-amber-300/20 via-amber-400/10 to-transparent blur-2xl opacity-60" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY GENIEX */}
      <section className="border-t border-line/70 bg-[var(--gx-black)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <SectionHeading eyebrow="Why GenieX" title={`In ${content.eyebrow.split(" · ")[0]}.`} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {LOCATION_WHY.map((w) => (
              <div key={w.title} className="rounded-2xl border border-line p-7 bg-[var(--gx-surface)]/40">
                <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold mb-5">
                  <w.Icon className="w-4 h-4" />
                </div>
                <h3 className="font-display text-lg tracking-tight">{w.title}</h3>
                <p className="text-dim text-[13px] mt-3 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {content.faqs?.length > 0 && (
        <section className="border-t border-line/70">
          <div className="max-w-[980px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
            <SectionHeading eyebrow="Questions" title="Frequently asked." />
            <div className="mt-14 divide-y divide-line/60 border-y border-line/60">
              {content.faqs.map((f, i) => (
                <FaqRow key={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative border-t border-line/70">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-10 py-20 lg:py-28 text-center">
          <p className="eyebrow mb-4">Local Team · Same-Week Service</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-[24ch] mx-auto">
            Begin your <span className="text-gold">intelligent living</span> journey here.
          </h2>
          <p className="lead mt-7 max-w-[52ch] mx-auto">
            Free site survey across {content.eyebrow.split(" · ")[0]}. Transparent pricing in ₹. 5-year warranty in writing.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <button onClick={onBook} data-testid="cta-book" className="btn-gold">
              Book Free Site Survey <ArrowRight className="w-4 h-4" />
            </button>
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="btn-outline">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <Link to="/dynamic-series" className="btn-outline">Explore Dynamic Series</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

const LOCATION_WHY = [
  { Icon: HardHat, title: "Local installers", body: "Dedicated installation team based in your city — never sub-contracted, never delayed." },
  { Icon: ShieldCheck, title: "5-year warranty", body: "On every component. On the installation. In writing." },
  { Icon: Headphones, title: "Same-week service", body: "Local technicians visit within 5–7 days — guaranteed citywide." },
  { Icon: Sparkles, title: "Apple-grade design", body: "Beautiful from across the room. Or it doesn't ship." },
];

function SkylineGraphic() {
  return (
    <div className="absolute inset-x-0 bottom-0 h-[55%] overflow-hidden pointer-events-none opacity-60">
      <svg viewBox="0 0 1600 600" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(212,175,55,0.12)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </linearGradient>
        </defs>
        <rect width="1600" height="600" fill="url(#sky)" />
        {/* skyline buildings */}
        <g fill="rgba(255,255,255,0.04)" stroke="rgba(212,175,55,0.18)" strokeWidth="1">
          <rect x="40" y="380" width="80" height="220" />
          <rect x="140" y="320" width="60" height="280" />
          <rect x="220" y="360" width="100" height="240" />
          <rect x="340" y="280" width="70" height="320" />
          <rect x="430" y="340" width="90" height="260" />
          <rect x="540" y="240" width="60" height="360" />
          <rect x="620" y="300" width="110" height="300" />
          <rect x="750" y="260" width="80" height="340" />
          <rect x="850" y="320" width="70" height="280" />
          <rect x="940" y="220" width="100" height="380" />
          <rect x="1060" y="290" width="80" height="310" />
          <rect x="1160" y="340" width="90" height="260" />
          <rect x="1270" y="260" width="70" height="340" />
          <rect x="1360" y="320" width="100" height="280" />
          <rect x="1480" y="300" width="80" height="300" />
        </g>
        {/* twinkling windows */}
        <g fill="rgba(212,175,55,0.4)">
          {Array.from({ length: 80 }).map((_, i) => (
            <rect key={i} x={(i * 21) % 1560 + 30} y={250 + ((i * 37) % 340)} width="3" height="3" />
          ))}
        </g>
      </svg>
    </div>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div className="max-w-[60ch]">
      <p className="eyebrow mb-4">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">{title}</h2>
    </div>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left py-6"
      data-testid={`faq-${slugify(q)}`}
    >
      <div className="flex items-center justify-between gap-6">
        <span className="font-display text-lg sm:text-xl tracking-tight text-white">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="text-dim text-[14.5px] leading-relaxed pt-4 max-w-[64ch]">{a}</p>
        </div>
      </div>
    </button>
  );
}

function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
